import React, { useState, useEffect, useCallback, useRef } from 'react';
import DOMPurify from 'dompurify';
import { useOutletContext } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Megaphone, Users, Calendar, Send, Upload, Download, Sparkles, Image as ImageIcon,
  Plus, Trash2, Edit3, Eye, Clock, CheckCircle2, RefreshCw, FileText, BarChart3, Mail,
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  mktDashboard, mktListContacts, mktAddContact, mktDeleteContact, mktImportContacts,
  mktContactsExportUrl, mktContactsTemplateUrl, mktListTemplates, mktListCampaigns,
  mktCreateCampaign, mktUpdateCampaign, mktDeleteCampaign, mktGetCampaign,
  mktPreviewAudience, mktSendTest, mktScheduleCampaign, mktCancelSchedule, mktSendNow,
  mktCampaignSends, mktUpload, mktAIContent, mktAIBanner,
} from '../../services/taskApi';

const STATUS_COLORS = {
  draft: 'bg-gray-500/15 text-gray-400',
  scheduled: 'bg-amber-500/15 text-amber-400',
  sending: 'bg-blue-500/15 text-blue-400',
  sent: 'bg-green-500/15 text-green-400',
};

const FlowMarketing = () => {
  const { isDark } = useOutletContext();
  const [tab, setTab] = useState('dashboard');
  const cardCls = isDark ? 'bg-[#0f1729] border-white/10' : 'bg-white border-gray-200';
  const inputCls = isDark ? 'bg-white/5 border-white/10 text-white placeholder:text-white/30' : '';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className={`text-xl font-bold flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          <Megaphone size={20} /> Email Marketing
        </h1>
      </div>

      <div className={`flex flex-col sm:flex-row gap-1 p-1 rounded-xl ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
        {[
          { k: 'dashboard', label: 'Dashboard', Icon: BarChart3 },
          { k: 'campaigns', label: 'Campaigns', Icon: Send },
          { k: 'contacts', label: 'Contacts', Icon: Users },
        ].map(t => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            data-testid={`mkt-tab-${t.k}`}
            className={`flex-1 px-4 py-2 rounded-lg text-xs font-medium transition-all flex items-center justify-center gap-2 ${
              tab === t.k
                ? isDark ? 'bg-white/10 text-white shadow' : 'bg-white text-gray-900 shadow-sm'
                : isDark ? 'text-white/40 hover:text-white/70' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <t.Icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'dashboard' && <Dashboard isDark={isDark} cardCls={cardCls} />}
      {tab === 'contacts' && <Contacts isDark={isDark} cardCls={cardCls} inputCls={inputCls} />}
      {tab === 'campaigns' && <Campaigns isDark={isDark} cardCls={cardCls} inputCls={inputCls} />}
    </div>
  );
};

// ============= DASHBOARD =============
const Dashboard = ({ isDark, cardCls }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    mktDashboard().then(r => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <p className={isDark ? 'text-white/40' : 'text-gray-500'}>Loading…</p>;

  const stats = [
    { label: 'Total Contacts', value: data.contacts, Icon: Users, color: 'blue' },
    { label: 'Campaigns', value: data.campaigns_total, Icon: Send, color: 'purple' },
    { label: 'Scheduled', value: data.scheduled, Icon: Calendar, color: 'amber' },
    { label: 'Sends (30d)', value: data.sends_last_30, Icon: Mail, color: 'sky' },
    { label: 'Delivered (30d)', value: data.delivered_last_30, Icon: CheckCircle2, color: 'green' },
    { label: 'Opened (30d)', value: data.opened_last_30, Icon: Eye, color: 'emerald' },
    { label: 'Open Rate', value: `${data.open_rate_pct}%`, Icon: BarChart3, color: 'pink' },
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {stats.map(s => (
          <Card key={s.label} className={cardCls}>
            <CardContent className="p-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className={`text-[10px] font-semibold uppercase tracking-wider ${isDark ? 'text-white/40' : 'text-gray-500'}`}>{s.label}</p>
                  <p className={`text-2xl font-bold mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{s.value}</p>
                </div>
                <s.Icon size={16} className={`text-${s.color}-400 mt-0.5`} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className={cardCls}>
        <CardContent className="p-4">
          <h2 className={`text-sm font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Recent campaigns</h2>
          {data.recent_campaigns.length === 0 ? (
            <p className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-500'}`}>No campaigns yet. Create one from the Campaigns tab.</p>
          ) : (
            <div className="space-y-2">
              {data.recent_campaigns.map(c => (
                <div key={c.id} className={`flex items-center justify-between gap-3 p-2.5 rounded-lg ${isDark ? 'bg-white/5' : 'bg-gray-50 border border-gray-100'}`}>
                  <div className="min-w-0">
                    <div className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{c.name}</div>
                    <div className={`text-[10px] ${isDark ? 'text-white/40' : 'text-gray-500'}`}>{c.subject || '— no subject —'}</div>
                  </div>
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${STATUS_COLORS[c.status] || STATUS_COLORS.draft}`}>{c.status}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ============= CONTACTS =============
const Contacts = ({ isDark, cardCls, inputCls }) => {
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', customer_name: '' });
  const fileRef = useRef(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await mktListContacts({ q });
      setList(r.data.contacts || []);
      setTotal(r.data.total || 0);
    } catch (err) { toast.error('Failed to load contacts'); }
    finally { setLoading(false); }
  }, [q]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await mktAddContact(form);
      toast.success('Contact saved');
      setForm({ name: '', email: '', customer_name: '' });
      setAddOpen(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Remove ${name || 'this contact'}?`)) return;
    await mktDeleteContact(id);
    toast.success('Removed');
    fetchData();
  };

  const handleImport = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const r = await mktImportContacts(f);
      toast.success(`Imported: ${r.data.created} new, ${r.data.updated} updated, ${r.data.skipped} skipped`);
      if (fileRef.current) fileRef.current.value = '';
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Import failed'); }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
        <Input className={`flex-1 h-9 ${inputCls}`} placeholder="Search by name / email / customer…" value={q} onChange={e => setQ(e.target.value)} data-testid="mkt-contacts-search" />
        <div className="flex gap-2 flex-wrap">
          <a href={mktContactsTemplateUrl()} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-3 h-9 rounded-lg text-xs font-medium border border-white/10 text-white/70 hover:bg-white/5" data-testid="mkt-template-download">
            <FileText size={12} /> Template
          </a>
          <a href={mktContactsExportUrl()} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-3 h-9 rounded-lg text-xs font-medium bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="mkt-export-btn">
            <Download size={12} /> Export
          </a>
          <label className="inline-flex items-center gap-1 px-3 h-9 rounded-lg text-xs font-medium bg-purple-600 hover:bg-purple-700 text-white cursor-pointer">
            <Upload size={12} /> Import CSV
            <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={handleImport} data-testid="mkt-import-input" />
          </label>
          <Button onClick={() => setAddOpen(true)} className="bg-blue-600 hover:bg-blue-700 h-9 px-4 text-xs" data-testid="mkt-add-contact-btn">
            <Plus size={12} className="mr-1" /> Add
          </Button>
        </div>
      </div>

      <p className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{total} contacts</p>

      {loading ? (
        <div className="space-y-2">{[...Array(3)].map((_, i) => <div key={i} className={`h-12 rounded animate-pulse ${isDark ? 'bg-white/5' : 'bg-gray-200'}`} />)}</div>
      ) : list.length === 0 ? (
        <Card className={cardCls}><CardContent className="p-8 text-center">
          <Users size={32} className={`mx-auto mb-2 ${isDark ? 'text-white/20' : 'text-gray-300'}`} />
          <p className={isDark ? 'text-white/40' : 'text-gray-500'}>No contacts. Add one or import a CSV.</p>
        </CardContent></Card>
      ) : (
        <Card className={cardCls}>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className={`border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                    <th className={`text-left p-3 font-medium uppercase tracking-wider text-[10px] ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Name</th>
                    <th className={`text-left p-3 font-medium uppercase tracking-wider text-[10px] ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Email</th>
                    <th className={`text-left p-3 font-medium uppercase tracking-wider text-[10px] ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Customer</th>
                    <th className={`text-right p-3 font-medium uppercase tracking-wider text-[10px] ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map(c => (
                    <tr key={c.id} className={`border-b last:border-b-0 ${isDark ? 'border-white/5' : 'border-gray-50'}`}>
                      <td className={`p-3 font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{c.name || '—'}</td>
                      <td className={`p-3 ${isDark ? 'text-white/60' : 'text-gray-600'}`}>{c.email}</td>
                      <td className={`p-3 ${isDark ? 'text-white/60' : 'text-gray-600'}`}>{c.customer_name || '—'}</td>
                      <td className="p-3 text-right">
                        <button onClick={() => handleDelete(c.id, c.name)} className={`p-1.5 rounded hover:bg-red-500/10 ${isDark ? 'text-red-400' : 'text-red-600'}`} data-testid={`mkt-delete-contact-${c.id}`}>
                          <Trash2 size={13} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className={`max-w-md ${isDark ? 'bg-[#0f1729] border-white/10 text-white' : ''}`} data-testid="mkt-contact-dialog">
          <DialogHeader><DialogTitle className={isDark ? 'text-white' : ''}>Add Contact</DialogTitle></DialogHeader>
          <form onSubmit={handleAdd} className="space-y-3 mt-2">
            <div>
              <Label className={isDark ? 'text-white/60' : ''}>Name</Label>
              <Input className={`mt-1 ${inputCls}`} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} data-testid="mkt-contact-name" />
            </div>
            <div>
              <Label className={isDark ? 'text-white/60' : ''}>Email *</Label>
              <Input type="email" className={`mt-1 ${inputCls}`} value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required data-testid="mkt-contact-email" />
            </div>
            <div>
              <Label className={isDark ? 'text-white/60' : ''}>Customer / Company</Label>
              <Input className={`mt-1 ${inputCls}`} value={form.customer_name} onChange={e => setForm(f => ({ ...f, customer_name: e.target.value }))} data-testid="mkt-contact-customer" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setAddOpen(false)} className={isDark ? 'border-white/20 text-white hover:bg-white/10' : ''}>Cancel</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="mkt-contact-save">Save</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============= CAMPAIGNS =============
const Campaigns = ({ isDark, cardCls, inputCls }) => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(null);  // campaign id being edited (null = none, 'new' = new campaign)

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await mktListCampaigns();
      setList(r.data || []);
    } catch (err) { toast.error('Failed to load campaigns'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (editor !== null) {
    return <CampaignEditor isDark={isDark} cardCls={cardCls} inputCls={inputCls}
      campaignId={editor === 'new' ? null : editor}
      onClose={() => { setEditor(null); fetchData(); }} />;
  }

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <p className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{list.length} campaigns</p>
        <Button onClick={() => setEditor('new')} className="bg-blue-600 hover:bg-blue-700 h-9 text-xs" data-testid="mkt-new-campaign-btn">
          <Plus size={12} className="mr-1" /> New Campaign
        </Button>
      </div>
      {loading ? (
        <div className="space-y-2">{[...Array(2)].map((_, i) => <div key={i} className={`h-14 rounded animate-pulse ${isDark ? 'bg-white/5' : 'bg-gray-200'}`} />)}</div>
      ) : list.length === 0 ? (
        <Card className={cardCls}><CardContent className="p-8 text-center">
          <Send size={32} className={`mx-auto mb-2 ${isDark ? 'text-white/20' : 'text-gray-300'}`} />
          <p className={isDark ? 'text-white/40' : 'text-gray-500'}>No campaigns yet</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {list.map(c => (
            <Card key={c.id} className={cardCls}>
              <CardContent className="p-3">
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{c.name}</span>
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${STATUS_COLORS[c.status] || STATUS_COLORS.draft}`}>{c.status}</span>
                    </div>
                    <div className={`text-[11px] mt-0.5 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{c.subject || '— no subject —'}</div>
                    {c.scheduled_for && (
                      <div className={`text-[10px] mt-0.5 ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>
                        <Clock size={9} className="inline mr-1" /> {new Date(c.scheduled_for).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Asia/Kolkata' })} IST
                      </div>
                    )}
                  </div>
                  <Button size="sm" variant="outline" onClick={() => setEditor(c.id)} className={isDark ? 'border-white/20 text-white hover:bg-white/10 h-8 text-xs' : 'h-8 text-xs'} data-testid={`mkt-edit-campaign-${c.id}`}>
                    <Edit3 size={11} className="mr-1" /> Open
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// ============= CAMPAIGN EDITOR =============
const CampaignEditor = ({ isDark, cardCls, inputCls, campaignId, onClose }) => {
  const [c, setC] = useState({ name: '', subject: '', preheader: '', html_body: '', banner_url: '', video_url: '', pdf_url: '', pdf_filename: '', audience: { all: true, tags: [], contact_ids: [] }, scheduled_for: null, status: 'draft' });
  const [templates, setTemplates] = useState([]);
  const [audCount, setAudCount] = useState(null);
  const [sends, setSends] = useState(null);
  const [saving, setSaving] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiForm, setAiForm] = useState({ brief: '', tone: 'professional', call_to_action: 'View catalogue', engine: 'claude' });
  const [aiBusy, setAiBusy] = useState(false);
  const [bannerBusy, setBannerBusy] = useState(false);
  const [bannerPrompt, setBannerPrompt] = useState('');
  const [testTo, setTestTo] = useState('');
  const [schedDate, setSchedDate] = useState('');

  useEffect(() => {
    mktListTemplates().then(r => setTemplates(r.data || [])).catch(() => {});
    if (campaignId) {
      mktGetCampaign(campaignId).then(r => { setC(r.data); setSchedDate(r.data.scheduled_for ? r.data.scheduled_for.slice(0, 16) : ''); });
      mktCampaignSends(campaignId).then(r => setSends(r.data)).catch(() => {});
    }
  }, [campaignId]);

  const saveDraft = async () => {
    if (!c.name.trim()) { toast.error('Campaign name is required'); return; }
    setSaving(true);
    try {
      if (campaignId) {
        await mktUpdateCampaign(campaignId, c);
        toast.success('Saved');
      } else {
        const r = await mktCreateCampaign(c);
        toast.success('Campaign created');
        // load it as the editing one
        window.history.replaceState(null, '', `?id=${r.data.id}`);
        setC(r.data);
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const applyTemplate = (tmpl) => {
    setC(prev => ({ ...prev, subject: tmpl.subject || prev.subject, preheader: tmpl.preheader || prev.preheader, html_body: tmpl.html_body || prev.html_body }));
    toast.success(`Applied template: ${tmpl.name}`);
  };

  const previewAudience = async () => {
    if (!campaignId) { toast.error('Save the draft first'); return; }
    try {
      const r = await mktPreviewAudience(campaignId);
      setAudCount(r.data.count);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const runAI = async (e) => {
    e.preventDefault();
    setAiBusy(true);
    try {
      const r = await mktAIContent(aiForm);
      setC(prev => ({ ...prev, subject: r.data.subject || prev.subject, preheader: r.data.preheader || prev.preheader, html_body: r.data.html_body || prev.html_body }));
      setAiOpen(false);
      toast.success('AI content applied');
    } catch (err) { toast.error(err.response?.data?.detail || 'AI failed'); }
    finally { setAiBusy(false); }
  };

  const runBanner = async () => {
    if (!bannerPrompt.trim()) { toast.error('Describe the banner you want'); return; }
    setBannerBusy(true);
    try {
      const r = await mktAIBanner(bannerPrompt.trim());
      setC(prev => ({ ...prev, banner_url: r.data.url }));
      toast.success('Banner generated');
    } catch (err) { toast.error(err.response?.data?.detail || 'Banner failed'); }
    finally { setBannerBusy(false); }
  };

  const uploadBanner = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const r = await mktUpload(f, 'banner');
      setC(prev => ({ ...prev, banner_url: r.data.url }));
      toast.success('Banner uploaded');
    } catch (err) { toast.error('Upload failed'); }
  };

  const uploadVideo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const r = await mktUpload(f, 'video');
      setC(prev => ({ ...prev, video_url: r.data.url }));
      toast.success('Video uploaded');
    } catch (err) { toast.error('Upload failed'); }
  };

  const htmlBodyRef = useRef(null);

  const insertInlineImage = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) { toast.error('Image must be under 2 MB for good email deliverability'); return; }
    try {
      const r = await mktUpload(f, 'content');
      const base = process.env.REACT_APP_BACKEND_URL || '';
      const fullUrl = r.data.url.startsWith('http') ? r.data.url : `${base}${r.data.url}`;
      const imgTag = `<img src="${fullUrl}" alt="${(f.name || 'image').replace(/"/g, '')}" style="max-width:100%;height:auto;border-radius:6px;margin:12px 0;" />`;
      // Insert at cursor if textarea is focused; else append.
      const ta = htmlBodyRef.current;
      if (ta && document.activeElement === ta) {
        const start = ta.selectionStart, end = ta.selectionEnd;
        const before = c.html_body.slice(0, start);
        const after = c.html_body.slice(end);
        const next = `${before}${imgTag}${after}`;
        setC(prev => ({ ...prev, html_body: next }));
        // Restore caret after the inserted tag (next tick)
        setTimeout(() => { ta.focus(); ta.selectionStart = ta.selectionEnd = start + imgTag.length; }, 0);
      } else {
        setC(prev => ({ ...prev, html_body: (prev.html_body || '') + '\n' + imgTag }));
      }
      toast.success('Image inserted into HTML body');
    } catch { toast.error('Image upload failed'); }
    finally { e.target.value = ''; }  // allow re-selecting the same file
  };

  const insertUnsubToken = () => {
    const ta = htmlBodyRef.current;
    const token = '{{unsubscribe_url}}';
    const link = `<a href="${token}" style="color:#2E3A6F;">Unsubscribe</a>`;
    if (ta && document.activeElement === ta) {
      const start = ta.selectionStart, end = ta.selectionEnd;
      const next = c.html_body.slice(0, start) + link + c.html_body.slice(end);
      setC(prev => ({ ...prev, html_body: next }));
      setTimeout(() => { ta.focus(); ta.selectionStart = ta.selectionEnd = start + link.length; }, 0);
    } else {
      setC(prev => ({ ...prev, html_body: (prev.html_body || '') + '\n' + link }));
    }
    toast.success('Unsubscribe link inserted');
  };

  const uploadPdf = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error('PDF too large — keep under 8 MB to avoid mail rejection'); return; }
    try {
      const r = await mktUpload(f, 'pdf');
      setC(prev => ({ ...prev, pdf_url: r.data.url, pdf_filename: r.data.filename || f.name }));
      toast.success('PDF attached — will be included with every send');
    } catch (err) { toast.error('Upload failed'); }
  };

  const handleSendTest = async () => {
    if (!campaignId) { toast.error('Save the draft first'); return; }
    if (!testTo || !testTo.includes('@')) { toast.error('Enter a valid test email'); return; }
    try {
      const r = await mktSendTest(campaignId, testTo);
      toast.success(r.data.sent ? 'Test sent' : 'Sent — check email logs');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleSchedule = async () => {
    if (!campaignId) { toast.error('Save the draft first'); return; }
    if (!schedDate) { toast.error('Pick a date & time'); return; }
    try {
      // schedDate is "YYYY-MM-DDTHH:MM" local; convert to ISO with timezone
      const iso = new Date(schedDate).toISOString();
      const r = await mktScheduleCampaign(campaignId, iso);
      toast.success(r.data.message);
      const fresh = await mktGetCampaign(campaignId);
      setC(fresh.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleCancel = async () => {
    if (!window.confirm('Cancel the scheduled send?')) return;
    try {
      await mktCancelSchedule(campaignId);
      const fresh = await mktGetCampaign(campaignId);
      setC(fresh.data);
      toast.success('Schedule cancelled');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleSendNow = async () => {
    if (!campaignId) { toast.error('Save first'); return; }
    if (!window.confirm('Send this campaign to the entire audience NOW?')) return;
    try {
      const r = await mktSendNow(campaignId);
      toast.success(r.data.message);
      const fresh = await mktGetCampaign(campaignId);
      setC(fresh.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const isReadOnly = ['sending', 'sent'].includes(c.status);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Button onClick={onClose} variant="outline" className={isDark ? 'border-white/20 text-white hover:bg-white/10 h-9 text-xs' : 'h-9 text-xs'}>← Back</Button>
        <div className="flex items-center gap-2">
          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${STATUS_COLORS[c.status] || STATUS_COLORS.draft}`}>{c.status}</span>
          <Button onClick={saveDraft} disabled={saving || isReadOnly} className="h-9 bg-blue-600 hover:bg-blue-700 text-xs" data-testid="mkt-save-campaign">
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>

      <Card className={cardCls}>
        <CardContent className="p-4 space-y-3">
          <div>
            <Label className={isDark ? 'text-white/60' : ''}>Campaign Name *</Label>
            <Input className={`mt-1 ${inputCls}`} value={c.name} onChange={e => setC({ ...c, name: e.target.value })} disabled={isReadOnly} data-testid="mkt-c-name" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className={isDark ? 'text-white/60' : ''}>Subject</Label>
              <Input className={`mt-1 ${inputCls}`} value={c.subject} onChange={e => setC({ ...c, subject: e.target.value })} disabled={isReadOnly} data-testid="mkt-c-subject" />
            </div>
            <div>
              <Label className={isDark ? 'text-white/60' : ''}>Preheader (preview text)</Label>
              <Input className={`mt-1 ${inputCls}`} value={c.preheader} onChange={e => setC({ ...c, preheader: e.target.value })} disabled={isReadOnly} />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => setAiOpen(true)} disabled={isReadOnly} className="h-8 px-3 text-xs bg-purple-600 hover:bg-purple-700" data-testid="mkt-ai-content-btn">
              <Sparkles size={12} className="mr-1" /> AI Content
            </Button>
          </div>
          {templates.length > 0 && (
            <div>
              <Label className={isDark ? 'text-white/60' : ''}>Quick templates (cutting-tools industry presets)</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {templates.map(t => (
                  <button key={t.id} type="button" onClick={() => applyTemplate(t)} disabled={isReadOnly}
                    className={`text-[10px] px-2 py-1 rounded-md border ${isDark ? 'border-white/10 text-white/70 hover:bg-white/5' : 'border-gray-200 text-gray-700 hover:bg-gray-50'}`}
                    data-testid={`mkt-tmpl-${t.id}`}>
                    {t.name}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <Label className={isDark ? 'text-white/60' : ''}>HTML Body</Label>
              <div className="flex items-center gap-2">
                <label className={`inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded cursor-pointer ${isDark ? 'bg-white/10 text-white/80 hover:bg-white/15' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-testid="mkt-insert-image-btn">
                  + Insert image
                  <input type="file" accept="image/*" className="hidden" onChange={insertInlineImage} disabled={isReadOnly} data-testid="mkt-insert-image-input" />
                </label>
                <button type="button" onClick={insertUnsubToken} disabled={isReadOnly}
                  className={`text-[10px] px-2 py-1 rounded ${isDark ? 'bg-white/10 text-white/80 hover:bg-white/15' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'} disabled:opacity-40`}
                  data-testid="mkt-insert-unsub-btn">
                  + Unsubscribe link
                </button>
              </div>
            </div>
            <textarea ref={htmlBodyRef} className={`mt-1 w-full min-h-[200px] rounded-lg p-3 text-xs font-mono ${isDark ? 'bg-white/5 border border-white/10 text-white' : 'bg-white border border-gray-300'}`}
              value={c.html_body} onChange={e => setC({ ...c, html_body: e.target.value })} disabled={isReadOnly} data-testid="mkt-c-html" />
            <p className={`mt-1 text-[10px] ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Personalize with {`{{name}}`}, {`{{customer_name}}`}, and {`{{unsubscribe_url}}`}. If you omit unsubscribe, one is auto-added on send (CAN-SPAM / GDPR compliance).</p>
          </div>

          {c.html_body && (
            <details className={`text-xs ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
              <summary className="cursor-pointer">Preview</summary>
              <div className={`mt-2 p-3 rounded border ${isDark ? 'border-white/10 bg-white' : 'border-gray-200 bg-white'}`} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(c.html_body.replace(/\{\{name\}\}/g, 'Alex').replace(/\{\{customer_name\}\}/g, 'Acme Mfg'), { USE_PROFILES: { html: true }, FORBID_TAGS: ['style', 'script', 'iframe', 'object', 'embed'], FORBID_ATTR: ['onerror', 'onload', 'onclick'] }) }} />
            </details>
          )}
        </CardContent>
      </Card>

      <Card className={cardCls}>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h3 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Banner, Video & PDF</h3>
            <span className={`text-[10px] ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Recommended sizes: <b>Banner</b> 1200×400 px (hero) or 1024×512 (content) · PNG/JPG &lt;500 KB · <b>PDF</b> &lt;8 MB</span>
          </div>
          <p className={`text-[11px] -mt-1 ${isDark ? 'text-emerald-300' : 'text-emerald-700'}`}>
            ℹ The banner you set here <b>replaces the default email header</b> for this campaign only. All non-marketing emails keep using the master banner from Email Branding.
          </p>
          <div className="flex flex-col sm:flex-row gap-2 flex-wrap">
            <Input className={`flex-1 ${inputCls}`} placeholder="AI banner prompt (e.g., carbide drill bits close-up on machined steel)"
              value={bannerPrompt} onChange={e => setBannerPrompt(e.target.value)} disabled={isReadOnly} data-testid="mkt-banner-prompt" />
            <Button onClick={runBanner} disabled={isReadOnly || bannerBusy} className="h-9 bg-pink-600 hover:bg-pink-700 text-xs" data-testid="mkt-ai-banner-btn">
              <ImageIcon size={12} className="mr-1" /> {bannerBusy ? 'Generating…' : 'AI Banner'}
            </Button>
            <label className={`inline-flex items-center gap-1 px-3 h-9 rounded-lg text-xs font-medium cursor-pointer ${isDark ? 'border border-white/20 text-white/70 hover:bg-white/5' : 'border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
              <Upload size={12} /> Upload Image
              <input type="file" accept="image/*" className="hidden" onChange={uploadBanner} disabled={isReadOnly} />
            </label>
            <label className={`inline-flex items-center gap-1 px-3 h-9 rounded-lg text-xs font-medium cursor-pointer ${isDark ? 'border border-white/20 text-white/70 hover:bg-white/5' : 'border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
              <Upload size={12} /> Video
              <input type="file" accept="video/*" className="hidden" onChange={uploadVideo} disabled={isReadOnly} />
            </label>
            <label className={`inline-flex items-center gap-1 px-3 h-9 rounded-lg text-xs font-medium cursor-pointer ${isDark ? 'border border-white/20 text-white/70 hover:bg-white/5' : 'border border-gray-200 text-gray-600 hover:bg-gray-50'}`} data-testid="mkt-pdf-upload-label">
              <Upload size={12} /> Attach PDF
              <input type="file" accept="application/pdf,.pdf" className="hidden" onChange={uploadPdf} disabled={isReadOnly} data-testid="mkt-pdf-upload" />
            </label>
          </div>
          {c.banner_url && (
            <div>
              <img src={c.banner_url} alt="banner" className="max-h-40 rounded border" />
              <p className={`text-[10px] mt-1 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>{c.banner_url}</p>
            </div>
          )}
          {c.video_url && <p className={`text-xs ${isDark ? 'text-white/60' : 'text-gray-600'}`}>Video: {c.video_url}</p>}
          {c.pdf_url && (
            <div className={`flex items-center justify-between gap-2 p-2 rounded ${isDark ? 'bg-white/5' : 'bg-gray-50 border border-gray-100'}`}>
              <span className={`text-xs ${isDark ? 'text-white/70' : 'text-gray-700'}`}>📎 {c.pdf_filename || 'attachment.pdf'}</span>
              <button type="button" onClick={() => setC(prev => ({ ...prev, pdf_url: '', pdf_filename: '' }))} disabled={isReadOnly}
                className={`text-[10px] ${isDark ? 'text-red-300 hover:text-red-200' : 'text-red-600 hover:text-red-700'}`}>Remove</button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className={cardCls}>
        <CardContent className="p-4 space-y-3">
          <h3 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Audience</h3>
          <label className={`flex items-center gap-2 text-xs cursor-pointer ${isDark ? 'text-white/70' : 'text-gray-700'}`}>
            <input type="checkbox" checked={!!c.audience?.all} onChange={e => setC({ ...c, audience: { ...c.audience, all: e.target.checked } })} disabled={isReadOnly} />
            Send to ALL active contacts
          </label>
          <div className="flex gap-2 items-center">
            <Button onClick={previewAudience} variant="outline" className={`h-8 text-xs ${isDark ? 'border-white/20 text-white hover:bg-white/10' : ''}`} data-testid="mkt-audience-preview-btn">
              Preview audience size
            </Button>
            {audCount !== null && <span className={`text-xs font-semibold ${isDark ? 'text-emerald-300' : 'text-emerald-700'}`}>{audCount} contacts will receive</span>}
          </div>
        </CardContent>
      </Card>

      <Card className={cardCls}>
        <CardContent className="p-4 space-y-3">
          <h3 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Test & Schedule</h3>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input className={`flex-1 ${inputCls}`} placeholder="your@email.com" type="email" value={testTo} onChange={e => setTestTo(e.target.value)} data-testid="mkt-test-to" />
            <Button onClick={handleSendTest} disabled={isReadOnly} variant="outline" className={`h-9 text-xs ${isDark ? 'border-white/20 text-white hover:bg-white/10' : ''}`} data-testid="mkt-send-test-btn">
              <Send size={12} className="mr-1" /> Send Test
            </Button>
          </div>
          {c.status === 'scheduled' ? (
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
              <p className={`text-xs flex-1 ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>
                <Clock size={11} className="inline mr-1" /> Scheduled for {new Date(c.scheduled_for).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Asia/Kolkata' })} IST · audience: {c.audience_count_at_schedule}
              </p>
              <Button onClick={handleCancel} variant="outline" className={`h-9 text-xs ${isDark ? 'border-red-500/40 text-red-300 hover:bg-red-500/10' : 'border-red-300 text-red-700 hover:bg-red-50'}`}>
                Cancel schedule
              </Button>
            </div>
          ) : !isReadOnly ? (
            <div className="flex flex-col sm:flex-row gap-2">
              <Input type="datetime-local" className={`flex-1 ${inputCls}`} value={schedDate} onChange={e => setSchedDate(e.target.value)} data-testid="mkt-sched-date" />
              <Button onClick={handleSchedule} className="h-9 bg-amber-600 hover:bg-amber-700 text-xs" data-testid="mkt-schedule-btn">
                <Calendar size={12} className="mr-1" /> Schedule
              </Button>
              <Button onClick={handleSendNow} className="h-9 bg-green-600 hover:bg-green-700 text-xs" data-testid="mkt-send-now-btn">
                <Send size={12} className="mr-1" /> Send Now
              </Button>
            </div>
          ) : null}
        </CardContent>
      </Card>

      {sends && sends.total > 0 && (
        <Card className={cardCls}>
          <CardContent className="p-4">
            <h3 className={`text-sm font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>Send analytics</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
              <Stat label="Total" value={sends.total} isDark={isDark} />
              <Stat label="Sent" value={sends.summary.sent} isDark={isDark} color="green" />
              <Stat label="Opened" value={sends.summary.opened} isDark={isDark} color="emerald" />
              <Stat label="Failed" value={sends.summary.failed} isDark={isDark} color="red" />
            </div>
          </CardContent>
        </Card>
      )}

      <Dialog open={aiOpen} onOpenChange={setAiOpen}>
        <DialogContent className={`max-w-md ${isDark ? 'bg-[#0f1729] border-white/10 text-white' : ''}`} data-testid="mkt-ai-dialog">
          <DialogHeader><DialogTitle className={isDark ? 'text-white' : ''}>Generate content with AI</DialogTitle></DialogHeader>
          <form onSubmit={runAI} className="space-y-3 mt-2">
            <div>
              <Label className={isDark ? 'text-white/60' : ''}>AI engine</Label>
              <select className={`mt-1 w-full h-9 rounded-lg text-xs px-2 ${isDark ? 'bg-white/5 border border-white/10 text-white' : 'bg-white border border-gray-300'}`}
                value={aiForm.engine} onChange={e => setAiForm({ ...aiForm, engine: e.target.value })} data-testid="mkt-ai-engine">
                <option value="claude">Claude (Anthropic)</option>
                <option value="chatgpt">ChatGPT (OpenAI GPT-5.4)</option>
                <option value="gemini">Gemini 3.1 Pro</option>
              </select>
            </div>
            <div>
              <Label className={isDark ? 'text-white/60' : ''}>Brief *</Label>
              <textarea className={`mt-1 w-full min-h-[100px] rounded-lg p-2 text-xs ${isDark ? 'bg-white/5 border border-white/10 text-white' : 'bg-white border border-gray-300'}`}
                placeholder="e.g., Announce our new TiAlN-coated end mill series for stainless and titanium, highlight 30% longer tool life and inventory-ready stock"
                value={aiForm.brief} onChange={e => setAiForm({ ...aiForm, brief: e.target.value })} required data-testid="mkt-ai-brief" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className={isDark ? 'text-white/60' : ''}>Tone</Label>
                <select className={`mt-1 w-full h-9 rounded-lg text-xs px-2 ${isDark ? 'bg-white/5 border border-white/10 text-white' : 'bg-white border border-gray-300'}`}
                  value={aiForm.tone} onChange={e => setAiForm({ ...aiForm, tone: e.target.value })}>
                  <option value="professional">Professional</option>
                  <option value="casual">Casual</option>
                  <option value="festive">Festive</option>
                  <option value="technical">Technical</option>
                </select>
              </div>
              <div>
                <Label className={isDark ? 'text-white/60' : ''}>CTA label</Label>
                <Input className={`mt-1 ${inputCls}`} value={aiForm.call_to_action} onChange={e => setAiForm({ ...aiForm, call_to_action: e.target.value })} />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setAiOpen(false)} className={isDark ? 'border-white/20 text-white hover:bg-white/10' : ''}>Cancel</Button>
              <Button type="submit" disabled={aiBusy} className="bg-purple-600 hover:bg-purple-700" data-testid="mkt-ai-generate">
                {aiBusy ? 'Generating…' : 'Generate'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Stat = ({ label, value, isDark, color = 'gray' }) => (
  <div className={`p-2 rounded ${isDark ? 'bg-white/5' : 'bg-gray-50 border border-gray-100'}`}>
    <div className={`text-[9px] uppercase tracking-wider ${isDark ? 'text-white/40' : 'text-gray-500'}`}>{label}</div>
    <div className={`text-lg font-bold text-${color}-400`}>{value}</div>
  </div>
);

export default FlowMarketing;
